/**
 * Parse alert rule API payloads for `source_refs` / `zone_refs` (tables + Sub source dialog).
 */

/** Normalize one API ref entry to `{ id, name }` for UI lists. */
export function normalizeRefItem(entry) {
    if (entry == null) return null;
    if (typeof entry === "string") {
      const t = String(entry).trim();
      return t ? { id: "", name: t } : null;
    }
    if (typeof entry === "object") {
      const rawName =
        entry.name ??
        entry.label ??
        entry.display_name ??
        entry.displayName ??
        entry.title ??
        entry.zone_name ??
        entry.camera_name;
      const id =
        entry.id != null && String(entry.id).trim() !== ""
          ? String(entry.id).trim()
          : "";
      const name =
        rawName != null && String(rawName).trim() !== ""
          ? String(rawName).trim()
          : "";
      if (!name && !id) return null;
      return { id, name: name || id };
    }
    return null;
  }
  
  export function dedupeRefItems(items) {
    const seen = new Set();
    const out = [];
    for (const x of items) {
      if (!x) continue;
      const key = `${x.id}|${x.name}`;
      if (seen.has(key)) continue;
      seen.add(key);
      out.push(x);
    }
    return out;
  }
  
  /** `source_refs: [{ id, name }, ...]` from list/detail payloads. */
  export function sourceRefsListFromApiRule(rule) {
    if (!rule || typeof rule !== "object") return [];
    const raw = rule.source_refs ?? rule.sourceRefs;
    if (!Array.isArray(raw)) return [];
    return raw.map(normalizeRefItem).filter(Boolean);
  }
  
  /** Zones nested on one `source_refs[]` element or singular `source_ref`. */
  export function zonesFromSourceRefEntry(entry) {
    if (!entry || typeof entry !== "object") return [];
    const out = [];
    const pushMany = (arr) => {
      if (!Array.isArray(arr)) return;
      for (const z of arr) {
        const e = normalizeRefItem(z);
        if (e) out.push(e);
      }
    };
    pushMany(entry.zone_refs ?? entry.zones ?? entry.zoneRefs);
    pushMany(entry.roi_refs ?? entry.rois ?? entry.rois_refs ?? entry.regions);
    const ne = normalizeRefItem(entry.zone_ref ?? entry.zoneRef);
    if (ne) out.push(ne);
    return dedupeRefItems(out);
  }
  
  /** Rule-level zones (including nested on singular `source_ref`). */
  export function ruleLevelZoneRefsOnlyFromApiRule(rule) {
    if (!rule || typeof rule !== "object") return [];
    const out = [];
    const pushMany = (arr) => {
      if (!Array.isArray(arr)) return;
      for (const z of arr) {
        const e = normalizeRefItem(z);
        if (e) out.push(e);
      }
    };
    pushMany(rule.zone_refs ?? rule.zoneRefs);
    const ze = normalizeRefItem(rule.zone_ref ?? rule.zoneRef);
    if (ze) out.push(ze);
    const rs = rule.source_ref;
    if (rs != null && typeof rs === "object") {
      pushMany(rs.zone_refs ?? rs.zones ?? rs.zoneRefs);
      const ne = normalizeRefItem(rs.zone_ref ?? rs.zoneRef);
      if (ne) out.push(ne);
    }
    return dedupeRefItems(out);
  }
  
  /** Raw zone entries at rule root (for parent-id matching). */
  function topLevelZoneObjectsFromRule(rule) {
    if (!rule || typeof rule !== "object") return [];
    const out = [];
    const raw = rule.zone_refs ?? rule.zoneRefs;
    if (Array.isArray(raw)) out.push(...raw);
    const zr = rule.zone_ref ?? rule.zoneRef;
    if (zr != null) out.push(zr);
    return out;
  }
  
  function zoneParentIdCandidates(z) {
    if (!z || typeof z !== "object") return [];
    const c = [];
    const keys = [
      "camera_id",
      "cameraId",
      "source_id",
      "sourceId",
      "source_ref_id",
      "sourceRefId",
      "parent_id",
      "parentId",
      "camera_ref_id",
      "cameraRefId",
    ];
    for (const k of keys) {
      if (z[k] != null && String(z[k]).trim() !== "") {
        c.push(String(z[k]).trim());
      }
    }
    return c;
  }
  
  /**
   * Attach rule-level zone objects to the source row whose `id` matches zone parent ids.
   */
  function assignTopLevelZonesToRows(rows, zoneObjects) {
    if (!Array.isArray(rows) || rows.length === 0 || !Array.isArray(zoneObjects)) {
      return;
    }
    const unassignedNorm = [];
    for (const obj of zoneObjects) {
      if (obj == null) continue;
      const norm = normalizeRefItem(obj);
      if (!norm) continue;
      const parents = zoneParentIdCandidates(obj);
      let placed = false;
      if (parents.length > 0) {
        for (const row of rows) {
          const rid = String(row.id || "").trim();
          if (rid && parents.some((p) => p === rid)) {
            row.zoneRefsList = dedupeRefItems([
              ...(row.zoneRefsList || []),
              norm,
            ]);
            placed = true;
            break;
          }
        }
      }
      if (!placed) unassignedNorm.push(norm);
    }
    if (unassignedNorm.length === 0) return;
    if (rows.length === 1) {
      rows[0].zoneRefsList = dedupeRefItems([
        ...(rows[0].zoneRefsList || []),
        ...unassignedNorm,
      ]);
      return;
    }
    rows[0].zoneRefsList = dedupeRefItems([
      ...(rows[0].zoneRefsList || []),
      ...unassignedNorm,
    ]);
  }
  
  /**
   * Accordion rows: each sub-source + zones (nested on each `source_refs[]`, or matched
   * from rule-level `zone_refs` by camera / source id).
   */
  export function sourceRefsWithZonesFromApiRule(rule) {
    if (!rule || typeof rule !== "object") return [];
    const raw = rule.source_refs ?? rule.sourceRefs;
    const topOrphans = ruleLevelZoneRefsOnlyFromApiRule(rule);
  
    if (Array.isArray(raw) && raw.length > 0) {
      const rows = raw
        .map((entry) => {
          const base = normalizeRefItem(entry);
          if (!base) return null;
          return {
            id: base.id,
            name: base.name,
            zoneRefsList: zonesFromSourceRefEntry(entry),
          };
        })
        .filter(Boolean);
  
      assignTopLevelZonesToRows(rows, topLevelZoneObjectsFromRule(rule));
  
      const allEmpty = rows.every(
        (r) => !r.zoneRefsList || r.zoneRefsList.length === 0,
      );
      if (allEmpty && topOrphans.length > 0) {
        if (rows.length === 1) {
          rows[0] = { ...rows[0], zoneRefsList: topOrphans };
        } else {
          rows[0] = {
            ...rows[0],
            zoneRefsList: dedupeRefItems([
              ...(rows[0].zoneRefsList || []),
              ...topOrphans,
            ]),
          };
        }
      }
      return rows;
    }
  
    const single = normalizeRefItem(rule.source_ref);
    if (single) {
      let zl = zonesFromSourceRefEntry(rule.source_ref);
      if (zl.length === 0) zl = [...topOrphans];
      else zl = dedupeRefItems([...zl, ...topOrphans]);
      return [{ id: single.id, name: single.name, zoneRefsList: zl }];
    }
  
    if (topOrphans.length > 0) {
      return [{ id: "", name: "Zones", zoneRefsList: topOrphans }];
    }
    return [];
  }
  
  export function zoneRefsListFromApiRule(rule) {
    if (!rule || typeof rule !== "object") return [];
    const out = [];
    const pushMany = (arr) => {
      if (!Array.isArray(arr)) return;
      for (const z of arr) {
        const e = normalizeRefItem(z);
        if (e) out.push(e);
      }
    };
    pushMany(rule.zone_refs ?? rule.zoneRefs);
    const single = rule.zone_ref ?? rule.zoneRef;
    const ze = normalizeRefItem(single);
    if (ze) out.push(ze);
  
    const rootSourceRef = rule.source_ref;
    if (rootSourceRef != null && typeof rootSourceRef === "object") {
      pushMany(rootSourceRef.zone_refs ?? rootSourceRef.zones);
      const nested = normalizeRefItem(
        rootSourceRef.zone_ref ?? rootSourceRef.zoneRef,
      );
      if (nested) out.push(nested);
    }
  
    const sources = rule.source_refs ?? rule.sourceRefs;
    if (Array.isArray(sources)) {
      for (const s of sources) {
        if (!s || typeof s !== "object") continue;
        out.push(...zonesFromSourceRefEntry(s));
      }
    }
    return dedupeRefItems(out);
  }
  
  export function sourceRefsNamesLabelFromApiRule(rule) {
    const list = sourceRefsListFromApiRule(rule);
    if (list.length === 0) return "";
    const names = list.map((x) => x.name).filter(Boolean);
    return names.length ? names.join(", ") : "";
  }
  